import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  Plus,
  Upload,
  Search,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  MapPin,
  Users,
  Filter,
  Eye,
  Database,
  RefreshCw,
  Check,
  AlertTriangle,
  X,
  ShieldCheck
} from 'lucide-react';
import backendApi from '../../services/backendApi';
import { useAuth } from '../../context/AuthContext';
import {
  getFallbackOnboardingSchema,
  isDemoTemplateRow,
  OnboardingSchemaMap,
  parseBooleanCell,
  parseExcelRows,
  parseNumberCell,
  pickRowValue,
  validateOnboardingRow,
} from '../../utils/adminBulkOnboarding';
import { DeletionProgressPopup } from '../../components/common/DeletionProgressPopup';
import {
  detectIntelligentDuplicates,
  normalizeDataResolution,
  generateDuplicateSummary,
} from '../../utils/smartDeduplication';

interface Site {
  site_id: string;
  site_name: string;
  site_code: string;
  site_type?: string;
  location?: string;
  capacity?: number;
  is_active: boolean;
  // New fields
  local_time_zone?: string | null;
  timezone_preference?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  altitude?: number | null;
  ppa_tariff?: number | null;
  plant_code?: number | null;
  currency?: string | null;
  plant_commissioning_date?: string | null;
  data_resolution?: string | null;
  site_manager_name?: string | null;
  site_manager_email?: string | null;
  site_manager_phone_no?: string | null;
  company_name?: string | null;
  company_code?: string | null;
  database_name?: string | null;
}

interface CompanyOption {
  company_id: number | string;
  company_name: string;
  company_code?: string;
}

const parseAliases = (aliases: any): string[] => {
  if (Array.isArray(aliases)) return aliases.map((a: any) => String(a).trim()).filter(Boolean);
  if (typeof aliases === 'string') return aliases.split(',').map((a: string) => a.trim()).filter(Boolean);
  return [];
};

const SiteManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingScopeFilters, setLoadingScopeFilters] = useState(false);
  const [isUploadingSites, setIsUploadingSites] = useState(false);
  const [deletingSiteId, setDeletingSiteId] = useState<string | null>(null);
  const [onboardingSchema, setOnboardingSchema] = useState<Partial<OnboardingSchemaMap>>(getFallbackOnboardingSchema());
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deleteStreamUrl, setDeleteStreamUrl] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const siteUploadInputRef = useRef<HTMLInputElement | null>(null);
  const hasInitializedScopedFilterFetch = useRef(false);
  const POLL_INTERVAL = 30000; // 30s scalable polling (prevents DB connection exhaustion)
  const [scopeFiltersReady, setScopeFiltersReady] = useState(false);

  // Database Mapping & Telemetry State
  const [showDbMappingModal, setShowDbMappingModal] = useState(false);
  const [dbMappings, setDbMappings] = useState<any[]>([]);
  const [loadingDbMappings, setLoadingDbMappings] = useState(false);
  const [editingSiteCode, setEditingSiteCode] = useState<string | null>(null);
  const [editDbName, setEditDbName] = useState('');
  const [editAliases, setEditAliases] = useState('');
  const [testingDbName, setTestingDbName] = useState<string | null>(null);
  const [bootstrappingDbName, setBootstrappingDbName] = useState<string | null>(null);
  const [dbHealthMap, setDbHealthMap] = useState<Record<string, any>>({});
  const [savingDbMapping, setSavingDbMapping] = useState(false);
  const [dbSearchTerm, setDbSearchTerm] = useState('');
  const [dbFeedback, setDbFeedback] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  const activeSiteId = useMemo(() =>
    Number(sessionStorage.getItem('site_id') || localStorage.getItem('site_id') || 0) || null,
    []);
  const activeSiteCode = useMemo(() =>
    sessionStorage.getItem('tenant') || localStorage.getItem('currentSite') || null,
    []);
  const roleName = String((currentUser as any)?.roleName || currentUser?.role || '').toLowerCase();
  const activeCompanyId =
    currentUser?.companyId ||
    Number(sessionStorage.getItem('company_id') || localStorage.getItem('company_id') || 0) ||
    null;
  const isSystemAdmin =
    !!currentUser?.isGlobalAdmin ||
    !!(currentUser as any)?.is_superuser ||
    !!(currentUser as any)?.is_super_admin ||
    ['system_admin', 'super_admin'].includes(roleName);

  // HO Admin can manage sites, Site Admin cannot
  // Use a more robust check: HO Admin is ho_admin role OR admin role without a specific site selected
  const isHoAdmin = roleName === 'ho_admin' || roleName === 'company_admin' || (roleName === 'admin' && !currentUser?.siteId);

  // Site Admin check for explicit exclusion: site_admin role OR admin role WITH a specific site selected
  const isSiteAdmin = roleName === 'site_admin' || (roleName === 'admin' && !!currentUser?.siteId);

  // Super Admin bypasses all restrictions, HO Admin can manage if not strictly a Site Admin
  const canManageSites = isSystemAdmin || (isHoAdmin && !isSiteAdmin);

  useEffect(() => {
    if (isSystemAdmin) {
      setCompanyFilter('all');
      setScopeFiltersReady(true);
      return;
    }

    setCompanyFilter(activeCompanyId ? String(activeCompanyId) : 'all');
    setScopeFiltersReady(true);
  }, [isSystemAdmin, activeCompanyId]);

  useEffect(() => {
    let mounted = true;

    const loadCompanies = async () => {
      if (!isSystemAdmin) {
        if (mounted) {
          setCompanies([]);
        }
        return;
      }

      setLoadingScopeFilters(true);
      const response = await backendApi.getCompanies();
      if (!mounted) {
        return;
      }

      if (response.success && Array.isArray(response.companies)) {
        setCompanies(response.companies);
      } else {
        setCompanies([]);
      }
      setLoadingScopeFilters(false);
    };

    loadCompanies();

    return () => {
      mounted = false;
    };
  }, [isSystemAdmin]);

  useEffect(() => {
    const loadOnboardingSchema = async () => {
      const response = await backendApi.getAdminOnboardingSchema();
      if (response.success && response.schema) {
        setOnboardingSchema(response.schema);
      }
    };

    void loadOnboardingSchema();
  }, []);

  // Initial load with loading state
  useEffect(() => {
    fetchSites(true);
  }, []);

  useEffect(() => {
    if (!scopeFiltersReady) {
      return;
    }

    if (!hasInitializedScopedFilterFetch.current) {
      hasInitializedScopedFilterFetch.current = true;
      return;
    }

    fetchSites(false);
  }, [companyFilter, scopeFiltersReady]);

  // Real-time polling
  useEffect(() => {
    startRealTimePolling();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [companyFilter]);

  const fetchSites = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }

      const requestedCompanyId = isSystemAdmin
        ? (companyFilter !== 'all' ? companyFilter : null)
        : (activeCompanyId ? String(activeCompanyId) : null);

      const response = await backendApi.getSites(false, '', requestedCompanyId);
if (response.success && response.sites) {
        // Map backend field names to frontend interface
        const mappedSites = response.sites.map((site: any) => ({
          ...site,
          site_id: String(site.site_id || site.id), // Ensure site_id is a string
          capacity: parseFloat(site.capacity_mw || site.capacity || 0) || 0, // Map capacity_mw to capacity and ensure it's a number
          site_name: site.site_name || 'Unnamed Site',
          site_code: site.site_code || 'N/A',
        }));
setSites(mappedSites);
      } else {
        console.error('Failed to fetch sites - response:', response);
        // Set empty array if fetch failed to avoid showing stale data
        setSites([]);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const startRealTimePolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      fetchSites(false);
    }, POLL_INTERVAL);
  };

  const fetchDbMappings = useCallback(async () => {
    try {
      setLoadingDbMappings(true);
      const res = await backendApi.getSiteDatabaseMappings();
      if (res.success && Array.isArray(res.mappings)) {
        setDbMappings(res.mappings);
        const health: Record<string, any> = {};
        res.mappings.forEach((m: any) => {
          if (m.health) {
            health[m.database_name || m.site_code] = m.health;
          }
        });
        setDbHealthMap(health);
      } else {
        setDbFeedback({ type: 'error', message: res.error || 'Failed to load database mappings' });
      }
    } catch (err: any) {
      setDbFeedback({ type: 'error', message: err?.message || 'Error fetching database mappings' });
    } finally {
      setLoadingDbMappings(false);
    }
  }, []);

  const filteredDbMappings = useMemo(() => {
    if (!dbSearchTerm.trim()) return dbMappings;
    const term = dbSearchTerm.toLowerCase();
    return dbMappings.filter((m: any) =>
      String(m.site_code || '').toLowerCase().includes(term) ||
      String(m.site_name || '').toLowerCase().includes(term) ||
      String(m.database_name || '').toLowerCase().includes(term) ||
      parseAliases(m.aliases).some((a: string) => a.toLowerCase().includes(term))
    );
  }, [dbMappings, dbSearchTerm]);

  const handleTestDbConnection = async (dbName: string, siteCode?: string) => {
    if (!dbName) return;
    try {
      setTestingDbName(dbName);
      setDbFeedback(null);
      const res = await backendApi.testDatabaseConnection(dbName);
      const healthData = res.data || res.health;
      if (res.success && healthData) {
        setDbHealthMap((prev) => ({ ...prev, [dbName]: healthData }));
        const isHealthy = healthData.tables_ready || healthData.healthy;
        const livedayCols = healthData.livedaycons_cols || healthData.axonmetric_livedaycons_cols || 0;
        const livestringCols = healthData.livestringinv_cols || healthData.axonmetric_livestringinv_cols || 0;
        if (isHealthy) {
          setDbFeedback({
            type: 'success',
            message: `Connection successful for "${dbName}"! Tables: ${livedayCols} cols in livedaycons, ${livestringCols} cols in livestringinv.`
          });
        } else if (healthData.exists || healthData.db_exists) {
          setDbFeedback({
            type: 'error',
            message: `Database "${dbName}" is online, but required telemetry tables are missing or incomplete. Click "Bootstrap Tables" to generate them.`
          });
        } else {
          setDbFeedback({
            type: 'error',
            message: `Health check issues for "${dbName}": ${healthData.error || healthData.issues?.join(', ') || 'Database not found on SQL Server'}`
          });
        }
      } else {
        setDbFeedback({ type: 'error', message: res.message || res.error || 'Test connection failed' });
      }
    } catch (err: any) {
      setDbFeedback({ type: 'error', message: err?.message || 'Test failed' });
    } finally {
      setTestingDbName(null);
    }
  };

  const handleBootstrapDbTables = async (dbName: string) => {
    if (!dbName) return;
    const confirmBootstrap = window.confirm(
      `Create telemetry tables (axonmetric_livedaycons & axonmetric_livestringinv) in database "${dbName}" if missing?`
    );
    if (!confirmBootstrap) return;

    try {
      setBootstrappingDbName(dbName);
      setDbFeedback(null);
      const res = await backendApi.bootstrapDatabaseTables(dbName);
      if (res.success) {
        setDbFeedback({
          type: 'success',
          message: res.message || `Telemetry tables bootstrapped successfully in "${dbName}".`
        });
        await handleTestDbConnection(dbName);
        await fetchDbMappings();
      } else {
        setDbFeedback({ type: 'error', message: res.error || 'Failed to bootstrap tables' });
      }
    } catch (err: any) {
      setDbFeedback({ type: 'error', message: err?.message || 'Bootstrap error' });
    } finally {
      setBootstrappingDbName(null);
    }
  };

  const startEditMapping = (item: any) => {
    setEditingSiteCode(item.site_code);
    setEditDbName(item.database_name || '');
    setEditAliases(parseAliases(item.aliases).join(', '));
    setDbFeedback(null);
  };

  const handleSaveDbMapping = async (siteCode: string) => {
    try {
      setSavingDbMapping(true);
      setDbFeedback(null);
      const cleanAliases = editAliases
        .split(',')
        .map((a) => a.trim().toLowerCase())
        .filter((a) => a.length > 0);

      const res = await backendApi.updateSiteDatabaseMapping({
        site_code: siteCode,
        database_name: editDbName.trim(),
        aliases: cleanAliases,
      });

      if (res.success) {
        setDbFeedback({
          type: 'success',
          message: `Successfully mapped site ${siteCode} to database "${res.database_name}".`
        });
        setEditingSiteCode(null);
        await fetchDbMappings();
        await fetchSites(false);
      } else {
        setDbFeedback({ type: 'error', message: res.error || 'Failed to save mapping' });
      }
    } catch (err: any) {
      setDbFeedback({ type: 'error', message: err?.message || 'Error saving mapping' });
    } finally {
      setSavingDbMapping(false);
    }
  };

  const handleDelete = async (siteId: string) => {
    if (!canManageSites) {
      alert('Site Admin cannot delete site');
      return;
    }
    
    if (window.confirm('Are you sure you want to permanently delete this site and all associated users and module data? This action cannot be undone.')) {
      setDeleteStreamUrl(`${process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || 'http://localhost:8000'}/api/auth/sites/${siteId}/delete/?stream=true`);
    }
  };

  const handleToggleActive = async (siteId: string, isActive: boolean) => {
    if (!canManageSites) {
      alert('Site Admin cannot change site status');
      return;
    }
    try {
      const response = await backendApi.activateSite(Number(siteId), !isActive);
      if (response.success) {
        setSites(sites.map(s =>
          String(s.site_id) === String(siteId)
            ? { ...s, is_active: !isActive }
            : s
        ));
        // Refresh data after update
        fetchSites(false);
      } else {
        alert(response.error || 'Failed to update site status');
      }
    } catch (error: any) {
      alert(error.message || 'Failed to update site status');
    }
  };

  const resolveCompanyId = (row: Record<string, any>): string | null => {
    const companyCode = String(pickRowValue(row, ['company_code'])).trim().toUpperCase();
    if (companyCode) {
      const company = companies.find((item) => String(item.company_code || '').toUpperCase() === companyCode);
      if (company) {
        return String(company.company_id);
      }
    }

    const companyName = String(pickRowValue(row, ['company_name'])).trim().toLowerCase();
    if (companyName) {
      const company = companies.find((item) => String(item.company_name || '').trim().toLowerCase() === companyName);
      if (company) {
        return String(company.company_id);
      }
    }

    // Backward-compatible fallback for older internal sheets that still carry DB IDs.
    const directCompanyId = String(pickRowValue(row, ['company_id'])).trim();
    if (directCompanyId) {
      return directCompanyId;
    }

    if (!isSystemAdmin && activeCompanyId) {
      return String(activeCompanyId);
    }

    return null;
  };

  const handleSiteUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canManageSites) {
      alert('You are not authorized to bulk onboard sites.');
      return;
    }

    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setIsUploadingSites(true);
    try {
      const rows = await parseExcelRows(file);
      if (rows.length === 0) {
        alert('No data rows found in uploaded Excel file.');
        return;
      }

      // Apply intelligent duplicate detection using composite key (site_name + location + company_name)
      const { validRows, duplicateRows, normalizedRows } = detectIntelligentDuplicates(rows, {
        checkFields: ['site_name', 'location', 'company_name'],
        normalizeFields: {
          data_resolution: (val) => normalizeDataResolution(val),
        },
        excludeDuplicates: true,
      });

      // Show duplicate summary if any found
      if (duplicateRows.length > 0) {
        const summary = generateDuplicateSummary(duplicateRows, ['site_name', 'location']);
        const duplicateMessage = summary.details.slice(0, 5).join('\n');
}

      // Use deduplicated and normalized rows
      const rowsToProcess = validRows.length > 0 ? validRows : normalizedRows;

      const failures: string[] = [];
      const skippedMessages: string[] = [];
      let successCount = 0;
      let actionableRows = 0;
      let deduplicatedCount = duplicateRows.length;
      let skippedDuplicates = 0;
      const isDuplicateValidationError = (message: string): boolean => {
        const msg = String(message || '').toLowerCase();
        const hasAlreadyExists = msg.includes('already exists');
        const hasSiteNameOrCode = msg.includes('site_name') || msg.includes('site code') || msg.includes('site_code');
        return hasAlreadyExists && hasSiteNameOrCode;
      };

      for (let index = 0; index < rowsToProcess.length; index += 1) {
        const row = rowsToProcess[index];
        // Find original row index for error reporting
        const originalRowIndex = rows.findIndex((r: any) => 
          String(r.site_name).trim().toLowerCase() === String(row.site_name).trim().toLowerCase() &&
          String(r.location || '').trim().toLowerCase() === String(row.location || '').trim().toLowerCase()
        );
        const rowNumber = originalRowIndex >= 0 ? originalRowIndex + 2 : index + 2;

        if (isDemoTemplateRow('sites', row, onboardingSchema)) {
          continue;
        }

        actionableRows += 1;

        const schemaErrors = validateOnboardingRow('sites', row, onboardingSchema);
        if (schemaErrors.length > 0) {
          failures.push(`Row ${rowNumber}: ${schemaErrors.join('; ')}`);
          continue;
        }

        const siteName = String(pickRowValue(row, ['site_name', 'name'])).trim();
        // Only honor explicit site_code from sheet. Avoid generic 'code' alias,
        // which can accidentally map unrelated columns and cause duplicate site_code errors.
        const siteCode = String(pickRowValue(row, ['site_code'])).trim().toUpperCase();
        const companyId = resolveCompanyId(row);

        if (!siteName || !companyId) {
          failures.push(`Row ${rowNumber}: site_name and company_name mapping are required`);
          continue;
        }

        // Skip only when both site_name and location already exist together.
        const siteLocation = String(pickRowValue(row, ['location'])).trim();
        const existingSiteByNameAndLocation = sites.find((s) =>
          String(s.site_name || '').trim().toLowerCase() === siteName.toLowerCase()
          && String(s.location || '').trim().toLowerCase() === siteLocation.toLowerCase()
        );

        if (existingSiteByNameAndLocation) {
          skippedDuplicates += 1;
          skippedMessages.push(`Row ${rowNumber}: â­ï¸ Skipped - Site already exists for same name + location ("${siteName}", "${siteLocation || 'N/A'}")`);
          continue;
        }

        // Use normalized data_resolution from deduplication process
        const dataResolution = row.data_resolution || normalizeDataResolution(pickRowValue(row, ['data_resolution']));

        const payload: Record<string, any> = {
          site_name: siteName,
          site_type: String(pickRowValue(row, ['site_type'])).trim() || null,
          location: siteLocation || null,
          description: String(pickRowValue(row, ['description'])).trim() || null,
          local_time_zone: String(pickRowValue(row, ['local_time_zone'])).trim() || null,
          timezone_preference: String(pickRowValue(row, ['timezone_preference'])).trim() || null,
          latitude: parseNumberCell(pickRowValue(row, ['latitude']), null),
          longitude: parseNumberCell(pickRowValue(row, ['longitude']), null),
          altitude: parseNumberCell(pickRowValue(row, ['altitude']), null),
          ppa_tariff: parseNumberCell(pickRowValue(row, ['ppa_tariff']), null),
          plant_code: parseNumberCell(pickRowValue(row, ['plant_code']), null),
          currency: String(pickRowValue(row, ['currency'])).trim() || 'INR',
          plant_commissioning_date: String(pickRowValue(row, ['plant_commissioning_date'])).trim() || null,
          data_resolution: dataResolution, // Use normalized value
          site_manager_name: String(pickRowValue(row, ['site_manager_name'])).trim() || null,
          site_manager_email: String(pickRowValue(row, ['site_manager_email'])).trim() || null,
          site_manager_phone_no: String(pickRowValue(row, ['site_manager_phone_no'])).trim() || null,
          company_id: companyId,
          is_active: parseBooleanCell(pickRowValue(row, ['is_active']), true),
        };

        const dbName = String(pickRowValue(row, ['database_name', 'db_name', 'sql_database'])).trim();
        if (dbName) {
          payload.database_name = dbName;
        }

        // Backward-compatible support for old sheets that still include site_code.
        if (siteCode) {
          payload.site_code = siteCode;
        }

        const capacityMw = parseNumberCell(pickRowValue(row, ['capacity_mw']), null);
        const capacityMwp = parseNumberCell(pickRowValue(row, ['capacity_mwp']), null);
        if (capacityMw !== null) {
          payload.capacity_mw = capacityMw;
        }
        if (capacityMwp !== null) {
          payload.capacity_mwp = capacityMwp;
        }

        const response = await backendApi.createSite(payload);
        if (response.success) {
          successCount += 1;
        } else {
          const backendError = String(response.error || 'create failed');
          if (isDuplicateValidationError(backendError)) {
            skippedDuplicates += 1;
            skippedMessages.push(
              `Row ${rowNumber}: â­ï¸ Skipped - Duplicate in DB (site_name: "${siteName}", site_code: "${siteCode || 'N/A'}", location: "${siteLocation || 'N/A'}")`
            );
            continue;
          }
          failures.push(`Row ${rowNumber}: ${backendError}`);
        }
      }

      await fetchSites(false);

      if (actionableRows === 0) {
        alert('No actionable rows found. Replace or delete the demo row in the template and upload again.');
        return;
      }

      const summary = [
        'ðŸ“Š Site Upload Report',
        `âœ“ Success: ${successCount}`,
        `âœ— Failed: ${failures.length}`,
        deduplicatedCount > 0 ? `⚠️ Deduplicated: ${deduplicatedCount} (using site_name + location)` : '',
        skippedDuplicates > 0 ? `â­ï¸ Skipped: ${skippedDuplicates} (already exist in database)` : '',
      ].filter(s => s);

      if (failures.length > 0) {
        summary.push('', 'First errors:', ...failures.slice(0, 6));
      }
      if (failures.length === 0 && skippedMessages.length > 0) {
        summary.push('', 'Skipped rows:', ...skippedMessages.slice(0, 6));
      }

      alert(summary.join('\n'));
    } catch (error: any) {
      alert(error?.message || 'Failed to process site upload file');
    } finally {
      setIsUploadingSites(false);
      event.target.value = '';
    }
  };

  const filteredSites = sites.filter(site => {
    const matchesSearch =
      site.site_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      site.site_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      site.location?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && site.is_active) ||
      (statusFilter === 'inactive' && !site.is_active);

    return matchesSearch && matchesStatus;
  });

  // Debug logging
  useEffect(() => {

}, [sites, filteredSites, searchTerm, statusFilter, companyFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center admin-shell">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading sites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.section initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="admin-hero p-4 sm:p-4 lg:p-5">
          <div className="admin-toolbar">
            <div className="space-y-2">
              <p className="admin-eyebrow">Site administration</p>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 rounded-[16px] bg-[linear-gradient(135deg,#0084CF,#1C4D88)] items-center justify-center text-white shadow-[0_12px_20px_rgba(0,132,207,0.2)]">
                  <Building2 size={22} />
                </div>
                <div>
                  <h1 className="admin-page-title">Operational sites arranged as deliberate horizontal records.</h1>
                  <p className="admin-page-subtitle mt-2">Replace card clutter with a cleaner directory that keeps company, location, capacity, and action controls aligned in a single readable row.</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {canManageSites && (
                <button
                  onClick={() => {
                    setShowDbMappingModal(true);
                    fetchDbMappings();
                  }}
                  className="admin-button admin-button-secondary"
                  title="Configure site database mappings, test SQL Server connectivity, and bootstrap telemetry tables" aria-label="Configure site database mappings, test SQL Server connectivity, and bootstrap telemetry tables"
                >
                  <Database size={16} />
                  Database Mappings
                </button>
              )}
              {canManageSites && (
                <>
                  <input
                    ref={siteUploadInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={handleSiteUpload}
                  />
                  <button
                    onClick={() => siteUploadInputRef.current?.click()}
                    className="admin-button admin-button-secondary"
                    disabled={isUploadingSites}
                  >
                    <Upload size={16} />
                    {isUploadingSites ? 'Uploading...' : 'Upload Data'}
                  </button>
                </>
              )}
              {canManageSites && (
                <button onClick={() => navigate('/admin/sites/create')} className="admin-button admin-button-primary">
                  <Plus size={18} />
                  Create site
                </button>
              )}
            </div>
          </div>

          <div className="admin-stat-strip mt-6">
            <div className="admin-stat-card">
              <p className="admin-stat-label">Sites</p>
              <p className="admin-stat-value">{filteredSites.length}</p>
              <p className="admin-stat-hint">Sites matching the active scope and filters</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Active</p>
              <p className="admin-stat-value">{filteredSites.filter((site) => site.is_active).length}</p>
              <p className="admin-stat-hint">Sites currently enabled</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Inactive</p>
              <p className="admin-stat-value">{filteredSites.filter((site) => !site.is_active).length}</p>
              <p className="admin-stat-hint">Sites paused or disabled</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Capacity</p>
              <p className="admin-stat-value">{filteredSites.reduce((sum, site) => sum + (typeof site.capacity === 'number' ? site.capacity : (site.capacity ? parseFloat(String(site.capacity)) : 0) || 0), 0).toFixed(2)}</p>
              <p className="admin-stat-hint">Combined installed MW</p>
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Filter sites</h2>
              <p className="admin-section-copy">Search by site name, code, or location and narrow by customer company or status.</p>
            </div>
          </div>
          <div className="admin-section-body">
            <div className="flex flex-col gap-4">
              {/* Row 1: Search & Reset */}
              <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
                <div className="admin-search-wrap w-full md:max-w-md">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Search sites by name, code, or location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="admin-input"
                  />
                </div>
                
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setCompanyFilter(isSystemAdmin ? 'all' : (activeCompanyId ? String(activeCompanyId) : 'all'));
                  }}
                  className="admin-button admin-button-ghost w-full md:w-auto xl:h-[46px] xl:px-4 xl:whitespace-nowrap"
                >
                  <Filter size={16} />
                  Reset filters
                </button>
              </div>

              {/* Row 2: Select Filters with clean labels */}
              <div className="flex flex-wrap gap-3 pt-3 border-t border-[#1C4D88]/5">
                {isSystemAdmin && (
                  <div className="w-full sm:w-auto min-w-[200px] flex-1">
                    <label className="block text-xs font-bold text-[#344B66] uppercase tracking-wider mb-1.5">
                      Company
                    </label>
                    <select
                      title="Customer Company Filter"
                      value={companyFilter}
                      onChange={(e) => setCompanyFilter(e.target.value)}
                      className="admin-select w-full"
                      disabled={loadingScopeFilters}
                    >
                      <option value="all">All customer companies</option>
                      {companies.map((company) => (
                        <option key={company.company_id} value={company.company_id}>
                          {company.company_name} {company.company_code ? `(${company.company_code})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="w-full sm:w-auto min-w-[140px] flex-1">
                  <label className="block text-xs font-bold text-[#344B66] uppercase tracking-wider mb-1.5">
                    Status
                  </label>
                  <select
                    title="Status Filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="admin-select w-full"
                  >
                    <option value="all">All status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {(isSystemAdmin || isHoAdmin) && companyFilter !== 'all' && (
                <span className="admin-pill admin-pill-info">Company scoped sites</span>
              )}
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Site directory</h2>
              <p className="admin-section-copy">Each site now reads as a single operational row instead of a detached card.</p>
            </div>
          </div>
          <div className="admin-section-body">
            {filteredSites.length === 0 ? (
              <div className="admin-empty">No sites matched the current filters.</div>
            ) : (
              <div className="admin-row-list">
                {filteredSites.map((site, index) => {
                  const isCurrentSite = String(site.site_id) === String(activeSiteId) || site.site_code === activeSiteCode;
                  const capacityValue = typeof site.capacity === 'number' ? site.capacity.toFixed(2) : (site.capacity ? parseFloat(String(site.capacity)).toFixed(2) : '0.00');

                  return (
                    <motion.div
                      key={site.site_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index, 8) * 0.04 }}
                      className={`admin-row ${isCurrentSite ? 'admin-row-highlight' : ''}`}
                    >
                      <div className="admin-row-leading">
                        <div className="admin-row-icon"><Building2 size={18} /></div>
                        <div className="min-w-0">
                          <p className="admin-row-title truncate">{site.site_name}</p>
                          <p className="admin-row-subtitle truncate">{site.location || 'Location not configured'}</p>
                          <div className="admin-row-tags">
                            <span className="admin-tag">{site.site_code}</span>
                            {site.company_name && <span className="admin-tag">{site.company_name}</span>}
                            {site.database_name && (
                              <span className="admin-tag font-mono text-[11px] text-[#1C4D88] bg-[#F2F8FC] border border-[#C2DFF1]" title={`Telemetry DB: ${site.database_name}`}>
                                DB: {site.database_name}
                              </span>
                            )}
                            {site.site_type && <span className="admin-tag capitalize">{site.site_type}</span>}
                            {isCurrentSite && <span className="admin-tag">Current session</span>}
                          </div>
                        </div>
                      </div>

                      <div className="admin-row-meta">
                        <div className="admin-meta-block">
                          <p className="admin-meta-label">Capacity</p>
                          <p className="admin-meta-value">{capacityValue} MW</p>
                        </div>
                        <div className="admin-meta-block">
                          <p className="admin-meta-label">Timezone</p>
                          <p className="admin-meta-value">{site.local_time_zone || site.timezone_preference || 'Not set'}</p>
                        </div>
                        <div className="admin-meta-block">
                          <p className="admin-meta-label">Manager</p>
                          <p className="admin-meta-value">{site.site_manager_name || site.site_manager_email || 'Not assigned'}</p>
                        </div>
                        <div className="admin-meta-block">
                          <p className="admin-meta-label">Status</p>
                          <p className="admin-meta-value">{site.is_active ? 'Active site' : 'Inactive site'}</p>
                        </div>
                      </div>

                      <div className="admin-row-actions">
                        {canManageSites ? (
                          <button
                            onClick={() => handleToggleActive(site.site_id, site.is_active)}
                            className={`admin-pill ${site.is_active ? 'admin-pill-success' : 'admin-pill-muted'}`}
                            disabled={!canManageSites}
                          >
                            {site.is_active ? 'Active' : 'Inactive'}
                          </button>
                        ) : (
                          <span className={`admin-pill ${site.is_active ? 'admin-pill-success' : 'admin-pill-muted'}`}>{site.is_active ? 'Active' : 'Inactive'}</span>
                        )}
                        <button onClick={() => navigate(`/admin/sites/${site.site_id}`)} className="admin-icon-button" title="View site details" aria-label="View site details">
                          <Eye size={16} />
                        </button>
                        {canManageSites && (
                          <button onClick={() => navigate(`/admin/sites/${site.site_id}/edit`)} className="admin-icon-button" title="Edit site" aria-label="Edit site">
                            <Edit size={16} />
                          </button>
                        )}
                        {canManageSites && (
                          <button 
                            onClick={() => handleDelete(site.site_id)} 
                            className="admin-icon-button text-[#B5475D]" 
                            title="Delete site" aria-label="Delete site"
                            disabled={deletingSiteId === site.site_id}
                            style={{
                              opacity: deletingSiteId === site.site_id ? 0.5 : 1,
                              cursor: deletingSiteId === site.site_id ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.section>
      </div>
      {/* Deletion Progress Popup */}
      <DeletionProgressPopup
        url={deleteStreamUrl || ''}
        isOpen={!!deleteStreamUrl}
        onClose={() => setDeleteStreamUrl(null)}
        onSuccess={() => fetchSites(false)}
      />

      {/* Site Telemetry Database Mapping Modal */}
      {showDbMappingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Modal Header */}
            <div className="p-5 border-b border-gray-100 flex items-start justify-between bg-gradient-to-r from-[#F2F8FC]/50 via-white to-gray-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#0066A3] text-white flex items-center justify-center shadow-md shadow-[#0084CF]/20">
                  <Database size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    Site Telemetry Database Mappings
                    <span className="text-xs font-normal text-[#1C4D88] bg-[#E3F0F9] px-2 py-0.5 rounded-full">
                      {dbMappings.length} Sites
                    </span>
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Map site codes to isolated SQL Server databases, verify table schema health, and bootstrap telemetry tables.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchDbMappings}
                  disabled={loadingDbMappings}
                  className="p-2 rounded-lg text-gray-500 hover:text-[#0066A3] hover:bg-[#F2F8FC] transition-colors"
                  title="Refresh mappings" aria-label="Refresh mappings"
                >
                  <RefreshCw size={17} className={loadingDbMappings ? 'animate-spin text-[#0066A3]' : ''} />
                </button>
                <button
                  onClick={() => {
                    setShowDbMappingModal(false);
                    setEditingSiteCode(null);
                    setDbFeedback(null);
                  }}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  title="Close modal" aria-label="Close modal"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Notification / Feedback Banner */}
            {dbFeedback && (
              <div
                className={`mx-5 mt-4 p-3 rounded-xl text-xs font-medium flex items-center justify-between border ${
                  dbFeedback.type === 'success'
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                    : dbFeedback.type === 'error'
                    ? 'bg-rose-50 text-rose-800 border-rose-200'
                    : 'bg-[#F2F8FC] text-[#1C4D88] border-[#C2DFF1]'
                }`}
              >
                <div className="flex items-center gap-2">
                  {dbFeedback.type === 'success' ? (
                    <CheckCircle size={16} className="text-emerald-600 shrink-0" />
                  ) : (
                    <AlertTriangle size={16} className="text-rose-600 shrink-0" />
                  )}
                  <span>{dbFeedback.message}</span>
                </div>
                <button
                  onClick={() => setDbFeedback(null)}
                  className="text-gray-400 hover:text-gray-600 p-0.5 rounded"
                >
                  <X size={14} />
                </button>
              </div>
            )}

            {/* Search Filter Bar */}
            <div className="px-5 pt-4 pb-2">
              <div className="relative">
                <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by site code, site name, or database name..."
                  value={dbSearchTerm}
                  onChange={(e) => setDbSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#0084CF]/20 focus:border-[#0084CF] transition-all"
                />
              </div>
            </div>

            {/* Mappings List Body */}
            <div className="p-5 overflow-y-auto space-y-3 flex-1">
              {loadingDbMappings && dbMappings.length === 0 ? (
                <div className="text-center py-12">
                  <RefreshCw size={28} className="animate-spin text-[#0066A3] mx-auto mb-3" />
                  <p className="text-xs text-gray-500">Loading database mappings & SQL Server status...</p>
                </div>
              ) : filteredDbMappings.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                  <Database size={28} className="text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">No matching site database records found.</p>
                </div>
              ) : (
                filteredDbMappings.map((item: any) => {
                  const isEditing = editingSiteCode === item.site_code;
                  const health = dbHealthMap[item.database_name || item.site_code] || item.health;
                  const isTesting = testingDbName === (item.database_name || item.site_code);
                  const isBootstrapping = bootstrappingDbName === (item.database_name || item.site_code);

                  return (
                    <div
                      key={item.site_code}
                      className="p-4 rounded-xl border border-gray-200 bg-white hover:border-[#C2DFF1] hover:shadow-sm transition-all space-y-3"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-2.5">
                          <span className="px-2.5 py-1 bg-gray-900 text-white font-mono text-xs font-semibold rounded-lg">
                            {item.site_code}
                          </span>
                          <div>
                            <span className="text-sm font-bold text-gray-800">{item.site_name}</span>
                            {item.company_name && (
                              <span className="text-xs text-gray-400 ml-2 font-normal">({item.company_name})</span>
                            )}
                          </div>
                        </div>

                        {/* Top action buttons */}
                        {!isEditing && (
                          <div className="flex items-center gap-1.5 self-end sm:self-auto">
                            {item.database_name && (
                              <>
                                <button
                                  onClick={() => handleTestDbConnection(item.database_name, item.site_code)}
                                  disabled={isTesting}
                                  className="px-2.5 py-1 text-xs font-medium text-[#1C4D88] bg-[#F2F8FC] border border-[#C2DFF1] hover:bg-[#E3F0F9] rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50"
                                  title="Ping SQL instance and verify table schema" aria-label="Ping SQL instance and verify table schema"
                                >
                                  <RefreshCw size={12} className={isTesting ? 'animate-spin' : ''} />
                                  {isTesting ? 'Testing...' : 'Test SQL'}
                                </button>
                                <button
                                  onClick={() => handleBootstrapDbTables(item.database_name)}
                                  disabled={isBootstrapping}
                                  className="px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50"
                                  title="Create telemetry tables in this database if missing" aria-label="Create telemetry tables in this database if missing"
                                >
                                  <ShieldCheck size={12} className={isBootstrapping ? 'animate-spin' : ''} />
                                  {isBootstrapping ? 'Bootstrapping...' : 'Bootstrap Tables'}
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => startEditMapping(item)}
                              className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1.5 transition-colors"
                              title="Edit database name or aliases" aria-label="Edit database name or aliases"
                            >
                              <Edit size={12} />
                              Edit
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Content / Edit Form */}
                      {isEditing ? (
                        <div className="p-3.5 bg-[#F2F8FC]/40 rounded-xl border border-[#E3F0F9] space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Target SQL Server Database Name:
                              </label>
                              <input
                                type="text"
                                value={editDbName}
                                onChange={(e) => setEditDbName(e.target.value)}
                                placeholder="e.g. axonmetric_dattainfradevikotbhakrani"
                                className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-mono text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0084CF]/20 focus:border-[#0084CF]"
                              />
                              <p className="text-[10px] text-gray-500 mt-1">
                                Enter exact database name hosted on the SQL Server instance.
                              </p>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">
                                Aliases (comma-separated):
                              </label>
                              <input
                                type="text"
                                value={editAliases}
                                onChange={(e) => setEditAliases(e.target.value)}
                                placeholder="e.g. bhakrani, devikot bhakrani"
                                className="w-full px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0084CF]/20 focus:border-[#0084CF]"
                              />
                              <p className="text-[10px] text-gray-500 mt-1">
                                Used to dynamically route URL slugs, site names, and query parameters.
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-end gap-2 pt-1">
                            <button
                              onClick={() => setEditingSiteCode(null)}
                              className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleSaveDbMapping(item.site_code)}
                              disabled={savingDbMapping}
                              className="px-4 py-1.5 text-xs text-white bg-[#0066A3] hover:bg-[#1C4D88] rounded-lg font-medium flex items-center gap-1.5 shadow-sm transition-colors disabled:opacity-50"
                            >
                              {savingDbMapping ? (
                                <RefreshCw size={12} className="animate-spin" />
                              ) : (
                                <Check size={12} />
                              )}
                              Save Mapping
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-gray-500">Database:</span>
                              {item.database_name ? (
                                <span className="font-mono text-[#1C4D88] bg-[#F2F8FC] border border-[#C2DFF1] px-2 py-0.5 rounded font-medium">
                                  {item.database_name}
                                </span>
                              ) : (
                                <span className="text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded font-medium">
                                  Unmapped (Auto-computed)
                                </span>
                              )}
                            </div>
                            {(() => {
                              const aliasesList = parseAliases(item.aliases);
                              if (aliasesList.length === 0) return null;
                              return (
                                <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                                  <span className="text-gray-400 text-[11px]">Aliases:</span>
                                  {aliasesList.map((alias: string) => (
                                    <span
                                      key={alias}
                                      className="text-[11px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded"
                                    >
                                      {alias}
                                    </span>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>

                          {/* Health status summary */}
                          {health && (() => {
                            const isReady = !!(health.tables_ready || health.healthy);
                            const dbExists = !!(health.exists || health.db_exists);
                            const dayCols = health.livedaycons_cols || health.axonmetric_livedaycons_cols || 0;
                            const invCols = health.livestringinv_cols || health.axonmetric_livestringinv_cols || 0;

                            return (
                              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-100 self-start sm:self-center">
                                <span
                                  className={`w-2 h-2 rounded-full ${
                                    isReady
                                      ? 'bg-emerald-500 ring-2 ring-emerald-200'
                                      : dbExists
                                      ? 'bg-amber-500 ring-2 ring-amber-200'
                                      : 'bg-rose-500 ring-2 ring-rose-200'
                                  }`}
                                />
                                <div className="text-[11px] text-gray-600 leading-tight">
                                  {isReady ? (
                                    <span className="text-emerald-700 font-semibold">
                                      SQL Online (Tables: {dayCols}c / {invCols}c)
                                    </span>
                                  ) : dbExists ? (
                                    <span className="text-amber-700 font-semibold">
                                      DB Online, Tables Need Bootstrap
                                    </span>
                                  ) : (
                                    <span className="text-rose-700 font-semibold">
                                      {health.error ? 'Connection Failed' : 'DB Not Created'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
              <span>
                Tip: When creating new sites, database names are automatically verified and tables bootstrapped.
              </span>
              <button
                onClick={() => {
                  setShowDbMappingModal(false);
                  setEditingSiteCode(null);
                  setDbFeedback(null);
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SiteManagement;


